import type { EmblaCarouselType, EmblaEventType } from "embla-carousel"

import * as React from "react"

import useEmblaCarousel from "embla-carousel-react"

// #region Noop Carousel
const noopCarousel: Carousel.CarouselAPI = {
  goTo: () => {},
  goToPreviousSlide: () => {},
  goToNextSlide: () => {},
  selectedScrollSnap: () => 0,
  scrollSnapList: () => [],
  onSelect: () => {},
  onReInitialize: () => {},
  onSlidesInView: () => {}
}

function createEmblaCarousel(api: EmblaCarouselType): Carousel.CarouselAPI {
  return {
    goTo: (index) => api.scrollTo(index),
    goToPreviousSlide: () => api.scrollPrev(),
    goToNextSlide: () => api.scrollNext(),
    selectedScrollSnap: () => api.selectedScrollSnap(),
    scrollSnapList: api.scrollSnapList,
    onSelect: (callback) => api.on("select", callback),
    onReInitialize: (callback) => api.on("reInit", callback),
    onSlidesInView: (callback) => api.on("slidesInView", callback)
  }
}
// #endregion

// #region Context
const CarouselContext = React.createContext<Carousel.Context>({
  ...noopCarousel,
  ref: () => {},
  slidesInView: []
})

const useCarousel = () => {
  const context = React.use(CarouselContext)
  if (context === null) {
    throw new Error("useCarousel must be used within a Carousel.Root")
  }
  return context
}

// #endregion

// #region Root
const Root = ({ children, onSlideChange, onSlideClick }: Carousel.Root) => {
  const [ref, api] = useEmblaCarousel({ align: "center", containScroll: false })
  const [_, setSelectedSnap] = React.useState(0)

  const [slidesInView, setSlidesInView] = React.useState<Array<number>>([])

  const carousel: Carousel.CarouselAPI = api ? createEmblaCarousel(api) : noopCarousel

  const updateSnap = () => {
    const newSnap = carousel.selectedScrollSnap()
    setSelectedSnap(newSnap)
    onSlideChange?.(newSnap)
  }

  carousel.onReInitialize(carousel.scrollSnapList)
  carousel.onReInitialize(updateSnap)
  carousel.onSelect(updateSnap)

  carousel.onSlidesInView((api) => setSlidesInView(api.slidesInView()))

  return (
    <CarouselContext
      value={{
        ref,
        ...carousel,
        onSlideClick,
        slidesInView
      }}
    >
      {children}
    </CarouselContext>
  )
}
// #endregion

// #region Namespace
namespace Carousel {
  type CarouselRef = <ViewportElement extends HTMLElement>(instance: ViewportElement | null) => void
  type CallbackType = (emblaApi: EmblaCarouselType, evt: EmblaEventType) => void
  export type Context = CarouselAPI & {
    ref: CarouselRef
    onSlideClick?: (index: number) => void
    slidesInView: Array<number>
  }
  export interface CarouselAPI {
    goTo: (index: number) => void
    goToPreviousSlide: () => void
    goToNextSlide: () => void
    selectedScrollSnap: () => number
    scrollSnapList: () => number[]
    onSelect: (callback: () => void) => void
    onReInitialize: (callback: () => void) => void
    onSlidesInView: (callback: CallbackType) => void
  }
  export type Root = {
    children: React.ReactNode
    onSlideChange?: (index: number) => void
    onSlideClick?: (index: number) => void
  }
}
// #endregion

// #region Exports
export const Carousel = {
  Root,
  useCarousel
}
// #endregion
